import { FeatureEntitlements } from '@/common/types/organisation';
import { ReactNode, useEffect, useState } from 'react';

let entitlementsLoaded = false;
let memoryEntitlements: FeatureEntitlements = {};

const listeners: ((entitlements: FeatureEntitlements) => void)[] = [];

const updateEntitlements = (updated: FeatureEntitlements) => {
  entitlementsLoaded = true;
  memoryEntitlements = updated;
  listeners.forEach((listener) => listener(updated));
};

export function useIsFeatureEnabled() {
  const [entitlements, setEntitlements] = useState(memoryEntitlements);

  useEffect(() => {
    const listener = (next: FeatureEntitlements) => setEntitlements(next);
    listeners.push(listener);
    return () => {
      const index = listeners.indexOf(listener);
      if (index > -1) listeners.splice(index, 1);
    };
  }, []);

  return (feature: keyof FeatureEntitlements) => {
    return !entitlementsLoaded || !!entitlements[feature];
  };
}

export function FeatureGateProvider({
  entitlements,
  children,
}: {
  entitlements: FeatureEntitlements;
  children: ReactNode;
}) {
  useEffect(() => {
    updateEntitlements(entitlements);
  }, [entitlements]);

  return children;
}

export function FeatureGate({
  feature,
  children,
}: {
  feature: keyof FeatureEntitlements;
  children: ReactNode;
}) {
  const isFeatureEnabled = useIsFeatureEnabled();

  return isFeatureEnabled(feature) ? children : null;
}

export function FeatureSwitch({
  feature,
  enabled,
  disabled,
}: {
  feature: keyof FeatureEntitlements;
  enabled: ReactNode;
  disabled: ReactNode;
}) {
  const isFeatureEnabled = useIsFeatureEnabled();

  return isFeatureEnabled(feature) ? enabled : disabled;
}
